// ウィジェットの箱([data-wu-widget])が無いページで動かす処理の入口。
//
// ウィジェットは要素を起点に繰り返すのに対し、フックはページそのものに対して高々1回判定する。
// 店側は目印を置く必要がなく、共通部に storefront.js が入っている時点で対象になる。

import { CONTACT_SITES } from './contact-pages'
import { SAMPLE_FORM_APP_SELECTOR, SAMPLE_FORM_SITES } from './sample-form'
import { SHORTAGE_PAGES } from '../hooks/stock-shortage-page'
import { PRICE_VARIATION_UNIT } from '../hooks/price-variation-note'

const SHORTAGE_PAGE_SITES: number[] = Object.keys(SHORTAGE_PAGES).map(Number)
const PRICE_VARIATION_NOTE_SITES: number[] = Object.keys(PRICE_VARIATION_UNIT).map(Number)

/** ページ内リンクのスクロールアニメ（hooks/anchor-scroll.ts）。紙ストロー(9) の旧 SiteCommon にだけあった */
export const ANCHOR_SCROLL_SITES: number[] = [9]

/** 商品ページの数量欄「？」ヘルプ（hooks/quantity-help.ts）。fukuro.in の旧 ProductDetails.ts にだけあった（Issue #78） */
export const QUANTITY_HELP_SITES: number[] = [1]

/**
 * 商品ページの価格注記 .price_desc を「枚数により単価が変わります。」にする店（hooks/price-desc-note.ts、Issue #78）。
 * おちゃのこの商品ページ欄に直書きがあった 3 店だけ。パックジャパン(6) は標準文言のままなので足さない
 */
export const PRICE_DESC_SITES: number[] = [1, 2, 4]

/**
 * 規格品サイズ検索のフリーページ（hooks/product-size-search.ts）。wuSiteResource2 の Shared/Search の引き取り（2026-09-23）。
 * 3 店 × 3 種別 = 9 ページ（1: /page/446,463,451  3: /page/147,148,149  6: /page/72,73,74）。ページ本文の
 * ng-app="ProductSearchApp" で判定するので、ページ番号はここに持たない
 */
export const PRODUCT_SIZE_SEARCH_SITES: number[] = [1, 3, 6]

/**
 * 営業日カレンダー（hooks/business-calendar.ts）。wuSiteResource2 の Shared/Calender の引き取り（2026-09-24）。
 * 配送案内のフリーページ 5 店 9 ページ（1: /page/564,1213  3: /page/350,405  6: /page/312,555  4: /page/138,143  11: /page/19）。
 * ページ本文の ng-app="CalenderApp" と .calendar で判定するので、ページ番号はここに持たない
 */
export const BUSINESS_CALENDAR_SITES: number[] = [1, 3, 6, 4, 11]

/**
 * アンケート（hooks/enquete.ts）。wuSiteResource2 の Shared/Enquete の引き取り（Issue #101）。
 * 依頼メールのリンク先のフリーページ 9 店 9 ページ（1: /page/1910  2: /page/171  3: /page/399  4: /page/200
 * 6: /page/542  9: /page/19  10: /page/60  11: /page/57  14: /page/61。EnqueteMailSettings.EnqueteUrl）。
 * ページ本文の ng-app="EnqueteApp" で判定するので、ページ番号はここに持たない
 */
export const ENQUETE_SITES: number[] = [1, 2, 3, 4, 6, 9, 10, 11, 14]

export interface HookEntry {
  name: string
  /** 発動する店。省略時は全店で判定する。 */
  sites?: number[]
  /**
   * チャンクを読む前に判定する軽い同期処理。DOM を見てもよいし location を見てもよい
   * （どちらも「読み込み前に判定できる」という点で同じなので、種類で分けない）。
   */
  when: () => boolean
  load: () => Promise<{ default: () => void }>
}

/**
 * 在庫表示（hooks/stock-check.ts）を wuStorefront 側で出す店（Issue #31）。
 * ★ここに店を足すのと、wuSiteResource2 の各店 ProductDetails.ts から checkStockRSP* / setupStockLimit の
 *   呼び出しを外すのを**同じ店で同時に**進める。片方だけだと二重に走る（5 分の重なりは許容）か、何も出なくなる。
 *   全店移ったら wuSiteResource2 側の関数と OppSite の StockCheck を消す。
 */
export const STOCK_CHECK_SITES: number[] = [
  1, // 2026-09-10: fukuro.in（wuSiteResource2 #18 と同時）
  10, // 2026-09-21: b-futou（Issue #48 の1店目。在庫表示 + カート制御）
  // 2026-09-21: 残りの店（Issue #48）。1店目で新旧一致とカート制御を実機確認してからまとめて足した。
  // 4（dansai）は現行も在庫を出していないので足さない。16（Lustia）は商品ページが ProductDetails.js を
  // 読んでおらず「新たに出す」ことになるため保留（田口さん確認待ち）
  2, 3, 6, 9, 11, 14, 15,
]

/**
 * 各店の SiteCommon（wuSiteResource2 の各店 SiteCommon.ts ＋ おちゃのこ「アクセス解析タグ設定」欄の直書き）を
 * wuStorefront 側で持つ店のうち、**5 本全部**（product-log / search-replace / cart-estimate / shipping-date-label /
 * sales-history-price）を持っていた店（Issue #53〜#60）。
 * contact-redirect だけは shared/contact-pages.ts の表（contact だけ先に移した店があるため）。
 * ★ 店を足す順序: ① ここに足してマージ（本番公開）→ ② wuSiteResource2 の同じ店の SiteCommon.ts を空にして配置
 *   → ③ 田口さんがおちゃのこの解析タグ欄を消す。逆順にすると問い合わせボタン（グローバル jumpContact が
 *   消える）と閲覧履歴が止まる。店ごとの差分（ページ番号・検索フォーム・API ホスト）は各 hook の表に足す。
 * ★ Issue #70 の残り 7 店は「現行どおり（今動いているものだけ移す）」。この表に足すと検索置換や productLog が
 *   その店で新しく動き始めてしまうので、**ここではなく下の hook ごとの表に足す**。
 * ★ OPP本舗(3) は sales-history-price 以外の 4 本 + cart-shipping-note を持つが、sales-history を動かさないためここには入れない
 */
export const SITE_COMMON_SITES: number[] = [
  2, // 2026-09-21: あんしんや（Issue #53 の1店目）
  4, // 2026-09-21: 台紙販売（Issue #55 の2店目。/contact 到達時のリダイレクトは同日あんしんやにも足した）
  6, // 2026-09-21: パックジャパン（Issue #58 の3店目。contact と検索置換はおちゃのこ側の直書きだった）
  1, // 2026-09-22: ワークアップ fukuro.in（Issue #60 の4店目・最後。contact と検索置換が TS 側、productLog と jumpContact の実体がおちゃのこ側）
]

/**
 * 見積書発行ボタン（hooks/cart-estimate.ts）を出す店。SITE_COMMON_SITES の 4 店 + Issue #70 の店。
 * ★ 過渡期（① 後 ③ 前）は旧実装と同居するが、#estimate が既にあれば足さないので 2 個にはならない
 */
export const CART_ESTIMATE_SITES: number[] = [
  ...SITE_COMMON_SITES,
  10, // 2026-09-23: b-futou（Issue #70 の1店目。contact は redirect だけ、検索置換は現行も無い）
  14, 15, 16, // 2026-09-23: きらきら / ボタンマート / Lustia（Issue #70 の2本目。b-futou と同形）
  11, 9, // 2026-09-23: コミック侍 / 紙ストロー（Issue #70 の3本目。+ contact リンクの付け替え。紙ストローは anchor-scroll と price-variation-note も）
  3, // 2026-09-23: OPP本舗（Issue #70 の4本目・最後。contact は #55 で移行済み。検索置換・productLog・/cart/1 の注記除去も）
]

/**
 * 検索キーワードの置換・記録（hooks/search-replace.ts）の店。SITE_COMMON_SITES の 4 店 + OPP本舗。
 * 店ごとのフォーム・API・一覧ページは search-replace.ts の SEARCH の表
 */
export const SEARCH_REPLACE_SITES: number[] = [...SITE_COMMON_SITES, 3]

/** /cart/1 の「※お届け日はご指定いただけません」除去（hooks/cart-shipping-note.ts）の店 */
export const CART_SHIPPING_NOTE_SITES: number[] = [
  6, // パックジャパン（Issue #58）
  3, // 2026-09-23: OPP本舗（Issue #70 の4本目。旧 removeCart1Text はパックジャパンと同じコード）
]

/** 配送日「指定なし(最短)」（hooks/shipping-date-label.ts）の店。今のところ CART_ESTIMATE_SITES と同じ顔ぶれ */
export const SHIPPING_DATE_LABEL_SITES: number[] = [...CART_ESTIMATE_SITES]

/**
 * 商品ページのサムネイルを押したら拡大表示も開く（hooks/thumbnail-zoom.ts、Issue #72）。
 * おちゃのこの自由記入欄に直書きがあった 7 店だけ（現行どおり）。2 / 3 / 4 / 6 はサムネイルはあるがこの動きを
 * 持っていないので足さない（足すとその店の挙動が変わる）。
 */
export const THUMB_ZOOM_SITES: number[] = [1, 9, 10, 11, 14, 15, 16]

/**
 * 閲覧履歴 Cookie productLog（hooks/product-log.ts）の店。SITE_COMMON_SITES の 4 店 + b-futou。
 * b-futou(10) は SiteCommon ではなく b-futou/ProductDetails.ts（中身は productLog だけ）からの引き取り
 * （Issue #70 追記・2026-09-23 田口さん了承）。「直前と同じ商品だけ積まない / セッション Cookie」の hook の形に揃える
 * （現行は「一度でも見た商品は積まない / 20 分」だったが、読み手のサンプル欄はどちらでも動く）
 */
export const PRODUCT_LOG_SITES: number[] = [
  ...SITE_COMMON_SITES,
  10, // 2026-09-23: b-futou（Issue #70 追記）
  14, 15, // 2026-09-23: きらきら / ボタンマート（Issue #70 の2本目。各店 ProductDetails.ts からの引き取り）
  11, 9, // 2026-09-23: コミック侍 / 紙ストロー（Issue #70 の3本目）
  3, // 2026-09-23: OPP本舗（Issue #70 の4本目。旧は jQuery.cookie で書いていたが規則は hook と同じ「末尾と違えば積む」）
  // 16（Lustia）は商品ページが ProductDetails.js を読んでおらず現行も productLog が動いていないので足さない
]

const PRODUCT_PAGE = /^\/product\/\d+\/?$/

/** 購入履歴の一覧（/sales-history）と詳細（/sales-history-detail/{id}） */
export const isSalesHistoryPath = (path: string): boolean => path.startsWith('/sales-history')

export const HOOKS: HookEntry[] = [
  {
    // お問い合わせのリンク/フォームを店のフリーページへ付け替える。/contact に到達したときのリダイレクトもここ。
    // ★ 対象店は SITE_COMMON_SITES ではなく shared/contact-pages.ts の表。OPP本舗(3) のように
    //   contact だけ先に移した店があるため（3 を SITE_COMMON_SITES に入れると他の hook が二重に動く）
    name: 'contact-redirect',
    sites: CONTACT_SITES,
    when: () =>
      /^\/contact(\/|$)/.test(location.pathname) ||
      !!document.querySelector('a[href*="/contact"],form[action*="/contact"],input[value="お問い合わせ"]'),
    load: () => import('../hooks/contact-redirect'),
  },
  {
    // 商品ページで閲覧履歴 Cookie（productLog）に商品 ID を積む
    name: 'product-log',
    sites: PRODUCT_LOG_SITES,
    when: () => PRODUCT_PAGE.test(location.pathname),
    load: () => import('../hooks/product-log'),
  },
  {
    // 商品検索の送信前にキーワードを SearchKey API で置き換え、入力語を記録する
    name: 'search-replace',
    sites: SEARCH_REPLACE_SITES,
    when: () => !!document.querySelector('form[action*="/product-list"] input[name="keyword"]'),
    load: () => import('../hooks/search-replace'),
  },
  {
    // /cart（#shoppingcart_page_step0）に「見積書発行」ボタンを足す
    name: 'cart-estimate',
    sites: CART_ESTIMATE_SITES,
    when: () => !!document.getElementById('shoppingcart_page_step0'),
    load: () => import('../hooks/cart-estimate'),
  },
  {
    // 配送日選択の「指定なし」→「指定なし(最短)」
    name: 'shipping-date-label',
    sites: SHIPPING_DATE_LABEL_SITES,
    when: () => !!document.querySelector('.shipping_date_selection'),
    load: () => import('../hooks/shipping-date-label'),
  },
  {
    // /cart/shipping のお届け日がカード補完で「翌月か翌々月の 1 日」に変わるのを防ぐ・カード払いの 1 日指定に警告（Issue #110）。
    // 対象は .shipping_date_selection のある店すべて（shipping-date-label と同じ顔ぶれ）。select の値には触らない
    name: 'cart-delivery-date-guard',
    sites: SHIPPING_DATE_LABEL_SITES,
    when: () => /^\/cart\/shipping\/?$/.test(location.pathname) && !!document.querySelector('.shipping_date_selection'),
    load: () => import('../hooks/cart-delivery-date-guard'),
  },
  {
    // /cart/1 の「※お届け日はご指定いただけません」を消す（表 CART_SHIPPING_NOTE_SITES の店）
    name: 'cart-shipping-note',
    sites: CART_SHIPPING_NOTE_SITES,
    when: () => /^\/cart\/1\/?$/.test(location.pathname), // 旧実装は href.indexOf('/cart/1')。末尾スラッシュも許す
    load: () => import('../hooks/cart-shipping-note'),
  },
  {
    // カートの「複数のお届け先」案内リンク（.senderinput の後ろ）。fukuro.in だけ
    name: 'cart-multi-delivery',
    sites: [1],
    when: () => /^\/cart\//.test(location.pathname), // 旧実装は if(location.href.indexOf('/cart/')) で常に true だった。/cart/ 配下に限定する
    load: () => import('../hooks/cart-multi-delivery'),
  },
  {
    // 購入履歴（/sales-history）の .priceb の class を外す（目的不明のまま現行どおり。hook 内のコメント参照）
    name: 'sales-history-price',
    sites: SITE_COMMON_SITES,
    when: () => location.pathname.includes('/sales-history'),
    load: () => import('../hooks/sales-history-price'),
  },
  {
    // 購入履歴の「もう一度購入する」を小窓ではなく商品ページ /product/{商品}（/v/ を外す）へ移す（Issue #86）。
    // 小窓経由だと在庫を超える数量が stock-check をすり抜けてカートに入るため、在庫のカート制御がある店だけ
    name: 'repurchase-to-product',
    sites: STOCK_CHECK_SITES,
    when: () => isSalesHistoryPath(location.pathname),
    load: () => import('../hooks/repurchase-to-product'),
  },
  {
    // 商品ページの在庫表示 + 在庫超過時のカート制御。STOCK_CHECK_SITES の店の /product/N だけ
    name: 'stock-check',
    sites: STOCK_CHECK_SITES,
    when: () => /^\/product\/\d+\/?$/.test(location.pathname),
    load: () => import('../hooks/stock-check'),
  },
  {
    // /cart（#shoppingcart_page_step0）で別注商品の有無を見て Cookie に保存する。
    name: 'cart-betchu-check',
    sites: [1, 3, 6], // fukuro.in / opp-honpo.net / pack-japan.jp
    when: () =>
      location.pathname === '/cart' && !!document.getElementById('shoppingcart_page_step0'),
    load: () => import('../hooks/cart-betchu-check'),
  },
  {
    // /cart/shipping（.shipping_date_selection）で、別注があれば選択可能日を絞る。
    name: 'cart-betchu-shipping',
    sites: [1, 3, 6],
    when: () => location.pathname === '/cart/shipping' && !!document.querySelector('.shipping_date_selection'),
    load: () => import('../hooks/cart-betchu-shipping'),
  },
  {
    // 在庫数量以上のお問い合わせフォーム（#zk_item）に直前の商品名を先入れ（fukuro.in。hooks/stock-check.ts と対）
    name: 'stock-inquiry-prefill',
    sites: [1],
    when: () => !/^\/product\/\d+\/?$/.test(location.pathname) && !!document.querySelector('#zk_item,[name="item"]'),
    load: () => import('../hooks/stock-inquiry-prefill'),
  },
  {
    // 「在庫数量以上ご希望のお客様」の案内ページを fukuro 方式にする（旧フォームを外してウィジェットの箱を置く）。
    // 対象は hooks/stock-shortage-page.ts の表。?p= が無ければ何もしない
    name: 'stock-shortage-page',
    sites: SHORTAGE_PAGE_SITES,
    when: () => Object.values(SHORTAGE_PAGES).some((p) => location.pathname === p || location.pathname === p + '/'),
    load: () => import('../hooks/stock-shortage-page'),
  },
  {
    // 商品ページのサムネイル → 拡大表示（直書きのあった 7 店）。旧の直書きが残るページでは hook 側が何もしない
    name: 'thumbnail-zoom',
    sites: THUMB_ZOOM_SITES,
    when: () => PRODUCT_PAGE.test(location.pathname) && !!document.querySelector('.thumbnail_image_link'),
    load: () => import('../hooks/thumbnail-zoom'),
  },
  {
    // ページ内リンク（a[href^="#"]）を 500ms でスクロール。紙ストローだけ（Issue #70 の3本目。フリーページに本物のページ内リンクがある）
    name: 'anchor-scroll',
    sites: ANCHOR_SCROLL_SITES,
    when: () => !!document.querySelector('a[href^="#"]'),
    load: () => import('../hooks/anchor-scroll'),
  },
  {
    // 商品ページの「販売価格は本数を選ぶと表示されます。」注記。表（PRICE_VARIATION_UNIT）にある店の /product/N だけ
    name: 'price-variation-note',
    sites: PRICE_VARIATION_NOTE_SITES,
    when: () => PRODUCT_PAGE.test(location.pathname) && !!document.querySelector('.selling_price'),
    load: () => import('../hooks/price-variation-note'),
  },
  {
    // 商品ページの数量欄に「？」ヘルプ（fancybox で注意画像）。fukuro.in だけ
    name: 'quantity-help',
    sites: QUANTITY_HELP_SITES,
    when: () => PRODUCT_PAGE.test(location.pathname) && !!document.getElementById('quantity_item'),
    load: () => import('../hooks/quantity-help'),
  },
  {
    // 商品ページの価格注記の文言（.price_desc）。直書きのあった 1 / 2 / 4 だけ
    name: 'price-desc-note',
    sites: PRICE_DESC_SITES,
    when: () => PRODUCT_PAGE.test(location.pathname) && !!document.querySelector('.price_desc'),
    load: () => import('../hooks/price-desc-note'),
  },
  {
    // 規格品サイズ検索（フリーページ 9 本）。旧 Search.js が居るページでは hook 側が何もしない
    name: 'product-size-search',
    sites: PRODUCT_SIZE_SEARCH_SITES,
    when: () => !!document.querySelector('[ng-app="ProductSearchApp"]'),
    load: () => import('../hooks/product-size-search'),
  },
  {
    // 営業日カレンダー（配送案内のフリーページ 9 本）。旧 Calender.js が居るページでは hook 側が何もしない
    name: 'business-calendar',
    sites: BUSINESS_CALENDAR_SITES,
    when: () => !!document.querySelector('[ng-app="CalenderApp"] .calendar'),
    load: () => import('../hooks/business-calendar'),
  },
  {
    // アンケート（依頼メールのリンク先のフリーページ 9 本）。旧 enquete.js が居るページでは hook 側が何もしない
    name: 'enquete',
    sites: ENQUETE_SITES,
    when: () => !!document.querySelector('[ng-app="EnqueteApp"]'),
    load: () => import('../hooks/enquete'),
  },
  {
    // サンプル請求・お問い合わせのフォーム（11 店のフリーページ。Issue #106）。旧 EcSiteForm.js が居るページでは hook 側が何もしない
    name: 'sample-form',
    sites: SAMPLE_FORM_SITES,
    when: () => !!document.querySelector(SAMPLE_FORM_APP_SELECTOR),
    load: () => import('../hooks/sample-form'),
  },
  {
    // 在庫表示の新旧比較（Issue #31 の検証用。?wu_stock_preview=1 が付いた商品ページだけ。切り替え後に消す）
    name: 'stock-check-preview',
    when: () =>
      /^\/product\/\d+\/?$/.test(location.pathname) && new URLSearchParams(location.search).has('wu_stock_preview'),
    load: () => import('../hooks/stock-check-preview'),
  },
  {
    // 共通表示枠（臨時メッセージ欄）。枠(div[data-wu-notice])が置かれたページなら全店・全ページ対象。
    name: 'notices',
    when: () => !!document.querySelector('div[data-wu-notice]'),
    load: () => import('../hooks/notices'),
  },
]

/** 既に発火した hook を二重に走らせないための印。 */
const RAN = new Set<string>()

export function runHooks(siteId: number | null, hooks: HookEntry[] = HOOKS): void {
  for (const hook of hooks) {
    if (RAN.has(hook.name)) continue
    if (hook.sites && (siteId === null || !hook.sites.includes(siteId))) continue

    // when()/load() の同期部分も try で囲む。ここが投げるとループ自体が止まり、
    // 後続の hook まで巻き添えにしてしまう（Promise の catch では拾えない）。
    try {
      if (!hook.when()) continue

      RAN.add(hook.name)
      hook
        .load()
        .then((m) => m.default())
        .catch((e) => {
          console.error(`[wu] hook の実行に失敗しました: "${hook.name}"`, e)
        })
    } catch (e) {
      console.error(`[wu] hook の判定に失敗しました: "${hook.name}"`, e)
    }
  }
}
